import React, { useEffect, useState, useRef } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { useColorScheme } from '@mui/joy/styles';
import {
    Box,
    Button,
    Typography,
    Stack,
    Card,
    CardContent,
    Grid,
    IconButton,
    Chip,
    AspectRatio,
    Divider,
    Tab,
    TabList,
    TabPanel,
    Tabs,
    Container,
    CircularProgress,
    Link
} from '@mui/joy';
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import {
    AutoAwesome,
    SmartToy,
    Psychology,
    Speed,
    Security,
    Language,
    Code,
    Cloud,
    Star,
    CheckCircle,
    ArrowForward,
    Lightbulb,
    PsychologyAlt,
    Science,
    Biotech,
    TrendingUp,
    People,
    Speed as SpeedIcon,
    Security as SecurityIcon,
    Settings,
    BubbleChart,
    DarkMode,
    LightMode,
    Gavel,
    Payment,
    Policy,
    CheckCircleOutline
} from '@mui/icons-material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import DialogTitle from '@mui/joy/DialogTitle';
import DialogContent from '@mui/joy/DialogContent';
import { Transition } from 'react-transition-group';

// Typing animation component
const TypewriterText = ({ text, delay = 0, sx = {} }) => {
    const [displayText, setDisplayText] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isWaiting, setIsWaiting] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [textHeight, setTextHeight] = useState(0);
    const textRef = useRef(null);

    useEffect(() => {
        if (textRef.current) {
            setTextHeight(textRef.current.offsetHeight);
        }
    }, [text]);

    useEffect(() => {
        if (isPaused) {
            const pauseTimeout = setTimeout(() => {
                setIsPaused(false);
            }, 1000);
            return () => clearTimeout(pauseTimeout);
        }

        if (isWaiting) {
            const waitTimeout = setTimeout(() => {
                setIsWaiting(false);
                setIsDeleting(true);
            }, 5000);
            return () => clearTimeout(waitTimeout);
        }

        if (isDeleting) {
            if (currentIndex === 0) {
                setIsDeleting(false);
                setIsPaused(true);
                return;
            }
            const timeout = setTimeout(() => {
                setDisplayText(prev => prev.slice(0, -1));
                setCurrentIndex(prev => prev - 1);
            }, 10);
            return () => clearTimeout(timeout);
        }

        if (currentIndex < text.length) {
            const timeout = setTimeout(() => {
                setDisplayText(prev => prev + text[currentIndex]);
                setCurrentIndex(prev => prev + 1);
            }, 20);
            return () => clearTimeout(timeout);
        } else {
            setIsWaiting(true);
        }
    }, [currentIndex, text, isDeleting, isWaiting, isPaused]);

    return (
        <Box sx={{ height: textHeight || 'auto', position: 'relative' }}>
            <Typography
                ref={textRef}
                level="h4"
                sx={{
                    position: 'absolute',
                    visibility: 'hidden',
                    ...sx,
                }}
            >
                {text}
            </Typography>
            <Typography
                level="h4"
                sx={{
                    ...sx,
                }}
            >
                {displayText}
                <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                    style={{ display: 'inline-block', marginLeft: '2px' }}
                >
                    |
                </motion.span>
            </Typography>
        </Box>
    );
};

// Stats counter component
const StatCounter = ({ end, duration = 2, prefix = '', suffix = '' }) => {
    const [count, setCount] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        const element = document.getElementById(`stat-${end}`);
        if (element) observer.observe(element);

        return () => {
            if (element) observer.unobserve(element);
        };
    }, [end]);

    useEffect(() => {
        if (isVisible) {
            let startTime = null;
            const animate = (currentTime) => {
                if (!startTime) startTime = currentTime;
                const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
                setCount(Math.floor(progress * end));
                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };
            requestAnimationFrame(animate);
        }
    }, [isVisible, end, duration]);

    return (
        <Typography
            id={`stat-${end}`}
            level="h2"
            sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #6a1b9a, #8e24aa)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent'
            }}
        >
            {prefix}{count}{suffix}
        </Typography>
    );
};

const priceVariants = [
    { label: 'RUB', free: '0₽', smart: '399₽', ultimate: '999₽', advanced: '1999₽', perMonth: '/мес.' },
    { label: 'BYN', free: '0 BYN', smart: '14.99 BYN', ultimate: '39.99 BYN', advanced: '74.99 BYN', perMonth: '/мес.' },
    { label: 'USD', free: '0$', smart: '4.99$', ultimate: '12.99$', advanced: '24.99$', perMonth: '/mo.' },
    { label: 'EUR', free: '0€', smart: '4.49€', ultimate: '11.99€', advanced: '22.99€', perMonth: '/mo.' },
];

const AnimatedPrice = ({ planKey }) => {
    const [currencyIdx, setCurrencyIdx] = React.useState(0);
    React.useEffect(() => {
        const interval = setInterval(() => {
            setCurrencyIdx(idx => (idx + 1) % priceVariants.length);
        }, 2500);
        return () => clearInterval(interval);
    }, []);
    const current = priceVariants[currencyIdx];
    return (
        <Typography sx={{ fontWeight: 700, fontSize: '2.1rem', mt: -1.2, mb: -1, color: '#7c4dff' }}>
            <AnimatePresence mode="wait" initial={false}>
                <motion.span
                    key={current[planKey] + current.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.35 }}
                    style={{ display: 'inline-block' }}
                >
                    {current[planKey]}
                </motion.span>
            </AnimatePresence>
            <span style={{ fontSize: '1rem', color: '#b39ddb', fontWeight: 400, marginLeft: 4 }}>
                {current.perMonth}
            </span>
        </Typography>
    );
};

// Animated background component
const AnimatedBackground = () => {
    const { scrollYProgress } = useScroll();
    const y = useTransform(scrollYProgress, [0, 1], [0, -1000]);
    const opacity = useTransform(scrollYProgress, [0, 0.5], [0.5, 0]);

    return (
        <Box
            component={motion.div}
            style={{ y, opacity }}
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'hidden',
                zIndex: 0,
                pointerEvents: 'none'
            }}
        >
            {[...Array(20)].map((_, i) => (
                <motion.div
                    key={i}
                    initial={{
                        x: Math.random() * window.innerWidth,
                        y: Math.random() * window.innerHeight,
                        scale: Math.random() * 0.5 + 0.5
                    }}
                    animate={{
                        y: [null, Math.random() * -100],
                        opacity: [0.2, 0.5, 0.2],
                    }}
                    transition={{
                        duration: Math.random() * 10 + 10,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    style={{
                        position: 'absolute',
                        width: '2px',
                        height: '2px',
                        background: 'linear-gradient(45deg, #6a1b9a, #8e24aa)',
                        borderRadius: '50%',
                        filter: 'blur(1px)'
                    }}
                />
            ))}
        </Box>
    );
};

// Floating card component
const FloatingCard = ({ children, delay = 0 }) => {
    return (
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay }}
            viewport={{ once: true }}
            whileHover={{ y: -5, scale: 1.02 }}
        >
            {children}
        </motion.div>
    );
};

const translations = {
    en: {
        release: 'SCREW LTD.',
        heroTitle: 'Capricorn AI — ',
        heroTitleHighlight: 'our most advanced system',
        heroTitleEnd: ' producing safer and more useful responses',
        heroSubtitle: 'A new level of safety, quality, and capabilities for your business and creativity.',
        tryButton: 'Try Capricorn AI',
        learnMore: 'Learn more',
        features: 'Features',
        benchmarks: 'Benchmarks',
        getStarted: 'Get Started',
        readyToExperience: 'Ready to experience the future of AI?',
        joinThousands: 'Join thousands of developers and businesses using Capricorn AI',
        login: 'LOG IN',
        understanding: 'Understanding',
        precision: 'Precision',
        vastness: 'Vastness',
        superiorUnderstanding: 'Superior Understanding',
        superiorUnderstandingDesc: 'Capricorn AI demonstrates exceptional context retention and understanding, maintaining coherent conversations across extended interactions while accurately tracking references and nuances.',
        versatileExcellence: 'Versatile Excellence',
        versatileExcellenceDesc: 'Capricorn AI excels across multiple domains, from code generation to image creation, delivering precise and efficient solutions for a wide range of tasks.',
        developerExperience: 'Developer Experience',
        developerExperienceDesc: 'Capricorn AI continuously evolves with developer-focused features, offering specialized modes and tools that enhance productivity and code quality.',
        input: 'Input',
        output: 'Output',
        modelComparison: 'Model Comparison',
        answerSpeed: 'Answer Speed',
        correctness: 'Correctness',
        activeUsers: 'Active Users',
        uptime: 'Uptime',
        apiCalls: 'API Calls',
        countries: 'Countries',
        testimonial1: '"Capricorn\'s ability to maintain context across complex technical discussions has transformed how we handle documentation and knowledge sharing. It\'s like having a technical expert who never forgets the details."',
        testimonial1Author: '— Maxim Yurievich, AI-developer',
        testimonial2: '"I use Capricorn AI daily and am impressed by the accuracy and detail of the answers provided. This AI demonstrates high efficiency in writing code. I apply it in various tasks, including text analysis, writing software code, solving historical tests, and mathematical problems. In addition, Capricorn AI surpasses ChatGPT and Midjourney in creating images from text prompts."',
        testimonial2Author: '— Alexander Vladimirovich, technical specialist',
        testimonial3: '"Capricorn AI is the most multifunctional and user-friendly neural network I have ever used. Each update expands the possibilities of interaction with the neural network, adding new useful functions. The developer mode is particularly noteworthy, as it greatly simplifies obtaining the most accurate and relevant answers. This is especially useful in the programming process, where I gain access to all the necessary materials."',
        testimonial3Author: '— Anton Sergeevich, .NET developer',
        featureTab1Input: 'Given a 2000-word technical document about quantum computing, summarize the key concepts and then answer specific questions about quantum entanglement.',
        featureTab1Output: 'The document discusses quantum computing fundamentals, including qubits, superposition, and entanglement. Quantum entanglement allows particles to be correlated regardless of distance, enabling quantum teleportation and secure communication. The system maintains context throughout the discussion, accurately referencing specific examples from the document.',
        featureTab2Input: 'Compare the performance characteristics of different database systems (PostgreSQL, MongoDB, Redis) for a high-traffic e-commerce application, including specific metrics and trade-offs.',
        featureTab2Output: 'PostgreSQL: ACID compliance, complex queries, 10k-50k TPS. MongoDB: Document storage, horizontal scaling, 100k+ TPS. Redis: In-memory, sub-millisecond latency, 1M+ TPS. Trade-offs: PostgreSQL for data integrity, MongoDB for flexibility, Redis for speed. System maintains precise technical accuracy and provides verifiable metrics.',
        featureTab3Input: 'Design a secure authentication system with OAuth 2.0 and OpenID Connect, including token management, refresh flows, and security considerations.',
        featureTab3Output: 'Implementation includes: OAuth 2.0 authorization code flow with PKCE, secure token storage, automatic refresh token rotation, CSRF protection, and rate limiting. Security measures: token encryption, short-lived access tokens (15min), secure HTTP-only cookies, and proper scope validation.',
        modelComparisonInput: 'Analyze the performance implications of using microservices architecture vs monolithic architecture for a high-traffic e-commerce platform, considering factors like scalability, maintainability, and operational complexity.',
        modelComparisonOutput4_3: 'Microservices offer better scalability but increase operational complexity. Monolithic is simpler to deploy but harder to scale. Consider team size and traffic patterns when choosing.',
        modelComparisonOutput4_4b: 'Microservices: Independent scaling (up to 100k+ RPS per service), 99.99% availability per service, but 30-40% higher operational overhead. Monolithic: Simpler deployment, 5-10k RPS limit, 99.9% availability, but scaling requires full application redeployment. Recommendation: Start monolithic, split into microservices when traffic exceeds 5k RPS or team size > 20 developers.',
        capricorn43: 'Capricorn 4.3',
        capricorn44: 'Capricorn 4.4',
        benchmarksTitle: 'Benchmarks',
        footerCopyright: 'SCREW LTD. © 2025. All rights reserved.',
        pricingTitle: 'Pricing Plans',
        pricingSubtitle: 'Choose the optimal plan for you and your business',
        pricingTabAI: 'Capricorn AI',
        pricingTabAPI: 'Capricorn API',
        planFree: 'Capricorn Free',
        planSmart: 'Capricorn Smart',
        planUltimate: 'Capricorn Ultimate',
        planAdvanced: 'Capricorn Advanced',
        priceFree: '0$',
        priceSmart: '4.99$',
        priceUltimate: '12.99$',
        priceAdvanced: '24.99$',
        perMonth: '/mo.',
        freeDesc: 'Basic free access',
        smartDesc: 'Creative access',
        ultimateDesc: 'Priority access',
        advancedDesc: 'Full access without limits',
        freeFeatures: [
            'Access to basic models',
            '10 requests per day',
            'Up to 300 characters per message',
            'Model updates',
            'Basic support',
            'Community access',
        ],
        smartFeatures: [
            'All Free features',
            '50 requests per day',
            'Up to 500 characters per message',
            'Image generation',
            'Audio generation',
            'Runtime utilities',
        ],
        ultimateFeatures: [
            'All Smart features',
            '200 requests per day',
            'Up to 1000 characters per message',
            'Priority server mode',
            'Dedicated servers under high load',
        ],
        advancedFeatures: [
            'All Ultimate features',
            'Unlimited characters',
            '1000 requests per day',
            'Developer mode',
            'Beta model access',
            'Extended technical support',
        ],
    },
    ru: {
        release: 'ACCEPTANCE GROUP',
        heroTitle: 'Capricorn AI — ',
        heroTitleHighlight: 'наша самая передовая система',
        heroTitleEnd: ' обеспечивающая более безопасные и полезные ответы',
        heroSubtitle: 'Новый уровень безопасности, качества и возможностей для вашего бизнеса и творчества.',
        tryButton: 'Попробовать Capricorn AI',
        learnMore: 'Узнать больше',
        features: 'Возможности',
        benchmarks: 'Тесты',
        getStarted: 'Присоединиться',
        readyToExperience: 'Готовы испытать будущее ИИ?',
        joinThousands: 'Присоединяйтесь к тысячам разработчиков и компаний, использующих Capricorn AI',
        login: 'ВОЙТИ',
        understanding: 'Понимание',
        precision: 'Точность',
        vastness: 'Масштаб',
        superiorUnderstanding: 'Превосходное понимание',
        superiorUnderstandingDesc: 'Capricorn AI демонстрирует исключительное сохранение контекста и понимание, поддерживая связные беседы в течение длительных взаимодействий, точно отслеживая ссылки и нюансы.',
        versatileExcellence: 'Универсальное превосходство',
        versatileExcellenceDesc: 'Capricorn AI преуспевает в различных областях, от генерации кода до создания изображений, предоставляя точные и эффективные решения для широкого спектра задач.',
        developerExperience: 'Опыт разработчика',
        developerExperienceDesc: 'Capricorn AI постоянно развивается с функциями, ориентированными на разработчиков, предлагая специализированные режимы и инструменты, повышающие производительность и качество кода.',
        input: 'Ввод',
        output: 'Вывод',
        modelComparison: 'Сравнение моделей',
        answerSpeed: 'Скорость ответа',
        correctness: 'Точность',
        activeUsers: 'Активных пользователей',
        uptime: 'Время работы',
        apiCalls: 'API вызовов',
        countries: 'Стран',
        testimonial1: '"Способность Capricorn сохранять контекст в сложных технических обсуждениях изменила то, как мы работаем с документацией и обменом знаниями. Это как иметь технического эксперта, который никогда не забывает детали."',
        testimonial1Author: '— Максим Юрьевич, AI-разработчик',
        testimonial2: '"Я ежедневно использую Capricorn AI и впечатлен точностью и детальностью предоставляемых ответов. Этот ИИ демонстрирует высокую эффективность в написании кода. Я применяю его в различных задачах, включая анализ текста, написание программного кода, решение исторических тестов и математических задач. Кроме того, Capricorn AI превосходит ChatGPT и Midjourney в создании изображений из текстовых запросов."',
        testimonial2Author: '— Александр Владимирович, технический специалист',
        testimonial3: '"Capricorn AI — это самая многофункциональная и удобная нейронная сеть, которую я когда-либо использовал. Каждое обновление расширяет возможности взаимодействия с нейронной сетью, добавляя новые полезные функции. Особенно стоит отметить режим разработчика, который значительно упрощает получение наиболее точных и релевантных ответов. Это особенно полезно в процессе программирования, где я получаю доступ ко всем необходимым материалам."',
        testimonial3Author: '— Антон Сергеевич, .NET разработчик',
        featureTab1Input: 'Дан технический документ объемом 2000 слов о квантовых вычислениях, обобщите ключевые концепции и ответьте на конкретные вопросы о квантовой запутанности.',
        featureTab1Output: 'Документ обсуждает основы квантовых вычислений, включая кубиты, суперпозицию и запутанность. Квантовая запутанность позволяет частицам быть коррелированными независимо от расстояния, что обеспечивает квантовую телепортацию и безопасную коммуникацию. Система сохраняет контекст на протяжении всего обсуждения, точно ссылаясь на конкретные примеры из документа.',
        featureTab2Input: 'Сравните характеристики производительности различных систем баз данных (PostgreSQL, MongoDB, Redis) для высоконагруженного электронного коммерческого приложения, включая конкретные метрики и компромиссы.',
        featureTab2Output: 'PostgreSQL: ACID-совместимость, сложные запросы, 10k-50k TPS. MongoDB: Документоориентированное хранение, горизонтальное масштабирование, 100k+ TPS. Redis: In-memory, задержка менее миллисекунды, 1M+ TPS. Компромиссы: PostgreSQL для целостности данных, MongoDB для гибкости, Redis для скорости. Система поддерживает точную техническую точность и предоставляет проверяемые метрики.',
        featureTab3Input: 'Разработайте безопасную систему аутентификации с использованием OAuth 2.0 и OpenID Connect, включая управление токенами, потоки обновления и соображения безопасности.',
        featureTab3Output: 'Реализация включает: поток кода авторизации OAuth 2.0 с PKCE, безопасное хранение токенов, автоматическую ротацию токенов обновления, защиту от CSRF и ограничение скорости. Меры безопасности: шифрование токенов, токены доступа с коротким сроком жизни (15 мин), безопасные HTTP-only куки и правильная валидация областей действия.',
        modelComparisonInput: 'Проанализируйте последствия производительности при использовании микросервисной архитектуры по сравнению с монолитной архитектурой для высоконагруженной платформы электронной коммерции, учитывая такие факторы, как масштабируемость, поддерживаемость и операционная сложность.',
        modelComparisonOutput4_3: 'Микросервисы обеспечивают лучшую масштабируемость, но увеличивают операционную сложность. Монолит проще развернуть, но сложнее масштабировать. Учитывайте размер команды и паттерны трафика при выборе.',
        modelComparisonOutput4_4b: 'Микросервисы: Независимое масштабирование (до 100k+ RPS на сервис), доступность 99.99% на сервис, но на 30-40% выше операционные расходы. Монолит: Проще развертывание, лимит 5-10k RPS, доступность 99.9%, но масштабирование требует полного переразвертывания приложения. Рекомендация: Начните с монолита, разделите на микросервисы, когда трафик превысит 5k RPS или размер команды > 20 разработчиков.',
        capricorn43: 'Capricorn 4.3',
        capricorn44: 'Capricorn 4.4',
        benchmarksTitle: 'Тесты производительности',
        footerCopyright: '© 2025 Acceptance Group. Все права защищены.',
        footerCompany: 'ООО "Акцептанс Групп"',
        footerUnp: 'УНП: 193855001',
        footerAddress: 'Адрес: 220021, Республика Беларусь, Заводской р-н., г. Минск, ул. Мичурина, д. 3, пом. 1',
        footerRegistration: 'Зарегистрирован в Мингорисполкоме 26.03.2025.',
        footerEmail: 'Email:',
        footerEmailValue: 'support@acceptancegroup.org',
        footerPhone: 'Телефон:',
        footerPhoneValue: '+375 (44) 702-34-12',
        footerWorkingHours: 'Режим работы: Ежедневно, с 9:00 до 21:00 часов',
        pricingTitle: 'Тарифы на услуги',
        pricingSubtitle: 'Выберите оптимальный тариф для вас и вашего бизнеса',
        pricingTabAI: 'Capricorn AI',
        pricingTabAPI: 'Capricorn API',
        planFree: 'Capricorn Free',
        planSmart: 'Capricorn Smart',
        planUltimate: 'Capricorn Ultimate',
        planAdvanced: 'Capricorn Advanced',
        priceFree: '0$',
        priceSmart: '4.99$',
        priceUltimate: '12.99$',
        priceAdvanced: '24.99$',
        perMonth: '/мес.',
        freeDesc: 'Бесплатный базовый доступ',
        smartDesc: 'Креативный доступ',
        ultimateDesc: 'Приоритетный доступ',
        advancedDesc: 'Полный доступ без ограничений',
        freeFeatures: [
            'Доступ к базовым моделям',
            '30 запросов в день',
            'Максимум 300 символов в сообщении',
            'Обновления моделей',
            'Базовая поддержка',
            'Доступ к сообществу',
        ],
        smartFeatures: [
            'Все функции Free',
            '70 запросов в день',
            'Максимум 500 символов в сообщении',
            'Генерация изображений',
            'Генерация аудио',
            'Runtime утилиты',
        ],
        ultimateFeatures: [
            'Все функции Smart',
            '200 запросов в день',
            'Максимум 1000 символов в сообщении',
            'Приоритетный режим на серверах',
            'Выделенные серверы при высокой нагрузке',
        ],
        advancedFeatures: [
            'Все функции Ultimate',
            'Неограниченное количество символов',
            '1000 запросов в день',
            'Режим разработчика',
            'Доступ к бета-версиям моделей',
            'Расширенная техническая поддержка',
        ],
    }
};

function Home() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useAuth();
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });
    const [tabIndex, setTabIndex] = useState(0);
    const tabsRef = useRef(null);
    const { mode, setMode } = useColorScheme();

    // Язык
    const isRussianDomain = window.location.hostname === 'chat.acceptancegroup.org' || window.location.hostname === 'localhost';
    const currentLanguage = isRussianDomain ? 'ru' : 'en';
    const t = translations[currentLanguage];
    const companyName = isRussianDomain ? 'Acceptance Group' : 'SCREW LTD.';

    const scrollToTabs = () => {
        tabsRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const stats = [
        { icon: <People />, value: 1000000, suffix: '+', label: t.activeUsers },
        { icon: <SpeedIcon />, value: 99, suffix: '%', label: t.uptime },
        { icon: <SecurityIcon />, value: 50, suffix: 'M+', label: t.apiCalls },
        { icon: <TrendingUp />, value: 200, suffix: '+', label: t.countries }
    ];

    // Update the featureTabs array to use translations
    const featureTabs = [
        {
            label: t.understanding,
            description: t.superiorUnderstandingDesc,
            input: t.featureTab1Input,
            output: t.featureTab1Output
        },
        {
            label: t.precision,
            description: t.versatileExcellenceDesc,
            input: t.featureTab2Input,
            output: t.featureTab2Output
        },
        {
            label: t.vastness,
            description: t.developerExperienceDesc,
            input: t.featureTab3Input,
            output: t.featureTab3Output
        }
    ];

    // Update the modelComparisons array to use translations
    const modelComparisons = [
        {
            label: t.input,
            value: t.modelComparisonInput
        },
        {
            label: t.output,
            value4_3: t.modelComparisonOutput4_3,
            value4_4b: t.modelComparisonOutput4_4b
        }
    ];

    // Update the benchmarks array to use translations
    const benchmarks = [
        {
            label: t.answerSpeed,
            value4_3: '16.3s',
            value4_4b: '6.1s'
        },
        {
            label: t.correctness,
            value4_3: '82%',
            value4_4b: '98%'
        }
    ];

    // Capricorn AI demo state
    const [response, setResponse] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");
    const [count, setCount] = React.useState(() => Number(sessionStorage.getItem('capricorn-demo-count') || 0));
    const [inputVisible, setInputVisible] = React.useState(true);
    const [expanded, setExpanded] = React.useState(false);

    // Следим за видимостью футера (и инпута)
    useEffect(() => {
        const footer = document.getElementById('footer');
        if (!footer) return;
        const observer = new window.IntersectionObserver(
            ([entry]) => setInputVisible(!entry.isIntersecting),
            { threshold: 0.1 }
        );
        observer.observe(footer);
        return () => observer.disconnect();
    }, []);

    // Для передачи expanded из FixedAskBlock
    const handleExpandChange = (val) => setExpanded(val);

    // Функция отправки сообщения в Capricorn AI
    const handleCapricornSubmit = async (message) => {
        if (count >= 5 || !message.trim()) return;
        setResponse(null);
        setError("");
        setLoading(true);
        const endpoint = isRussianDomain
            ? 'https://api.acceptancegroup.org/v3/ai/generate'
            : 'https://api.screwltd.com/v3/ai/generate';
        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-KEY': 'scr-432f5c61e712b0ffd08ea116573981d6'
                },
                body: JSON.stringify({
                    message,
                    version: 'Mini',
                    temperature: 0.7,
                    instructions: "Answer very concisely. Use as few words as possible. No explanations. If user asks about the model, say that it's Capricorn AI 4.4 (Demo)."
                })
            });
            const data = await res.json();
            if (data.text) {
                setResponse(data.text);
                const newCount = count + 1;
                setCount(newCount);
                sessionStorage.setItem('capricorn-demo-count', String(newCount));
            } else {
                setError(data.error || 'Ошибка при получении ответа');
            }
        } catch (e) {
            setError('Ошибка сети');
        }
        setLoading(false);
    };

    // Текст просьбы о регистрации
    const registerMsg = isRussianDomain
        ? 'Для продолжения зарегистрируйтесь.'
        : 'To continue please register or log in.';

    // Modal open states for legal links
    const [openPublic, setOpenPublic] = useState(false);
    const [openPayment, setOpenPayment] = useState(false);
    const [openPolicy, setOpenPolicy] = useState(false);
    const [openConsent, setOpenConsent] = useState(false);

    return (
        <>
            {/* Fixed Header */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(10, 10, 10, 0.7)'
                        : 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(12px)',
                    borderColor: 'divider',
                }}
            >
                <Container maxWidth="xl">
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 2,
                            px: { xs: 0, sm: 3 }
                        }}
                    >
                        {/* Logo with Icon */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <BubbleChart sx={{
                                color: '#CC99FF',
                                fontSize: '2rem'
                            }} />
                            <Typography
                                level="h4"
                                sx={{
                                    fontWeight: 700,
                                    background: '#CC99FF',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    color: 'transparent',
                                }}
                            >
                                Capricorn AI
                            </Typography>
                        </Box>

                        {/* Navigation Links */}
                        <Stack
                            direction="row"
                            spacing={3}
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                alignItems: 'center',
                            }}
                        >
                            <Button
                                variant="plain"
                                color="neutral"
                                onClick={scrollToTabs}
                                sx={{ fontWeight: 500, borderRadius: 250 }}
                            >
                                {t.features}
                            </Button>
                            <Button
                                variant="plain"
                                color="neutral"
                                onClick={() => window.scrollTo({ top: document.querySelector('#benchmarks').offsetTop - 80, behavior: 'smooth' })}
                                sx={{ fontWeight: 500, borderRadius: 25 }}
                            >
                                {t.benchmarks}
                            </Button>
                            <Button
                                variant="plain"
                                color="neutral"
                                onClick={() => {
                                    const el = document.querySelector('[data-pricing-block]');
                                    if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
                                }}
                                sx={{ fontWeight: 500, borderRadius: 250 }}
                            >
                                {t.pricingTitle}
                            </Button>
                            <Button
                                variant="plain"
                                color="neutral"
                                onClick={() => window.scrollTo({ top: document.querySelector('#cta').offsetTop - 80, behavior: 'smooth' })}
                                sx={{ fontWeight: 500, borderRadius: 25 }}
                            >
                                {t.getStarted}
                            </Button>
                        </Stack>

                        {/* Theme Switcher + Login Button */}
                        <Stack direction="row" spacing={1} alignItems="center">
                            <IconButton onClick={() => setMode(mode === 'light' ? 'dark' : 'light')} sx={{ borderRadius: '50%', width: 40, height: 40 }}>
                                {mode === 'light' ? <DarkMode /> : <LightMode />}
                            </IconButton>
                            <Button
                                variant="solid"
                                size="sm"
                                onClick={() => redirectToAuth(isRussianDomain)}
                                sx={{
                                    borderRadius: 99,
                                    px: 2,
                                    backgroundColor: '#6a1b9a',
                                    color: '#fff',
                                    '&:hover': {
                                        backgroundColor: '#7b1fa2',
                                    },
                                }}
                            >
                                {t.login}
                            </Button>
                        </Stack>
                    </Box>
                </Container>
            </Box>

            {/* Main Content */}
            <Box sx={{
                minHeight: '100vh',
                background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(to bottom, #0a0a0a, #1a1a1a)'
                    : 'linear-gradient(to bottom, #f7f7fa, #ffffff)',
                position: 'relative',
                pt: '72px', // Add padding for fixed header
                overflowX: 'hidden', // Prevent horizontal overflow
                width: '100%' // Ensure full width
            }}>
                {/* Progress bar */}
                <motion.div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #6a1b9a, #8e24aa)',
                        transformOrigin: '0%',
                        scaleX,
                        zIndex: 1001
                    }}
                />

                {/* Hero Section: smaller text, purple buttons */}
                <Box sx={{
                    minHeight: '40vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pt: { xs: 6, md: 8 },
                    pb: { xs: 6, md: 8 },
                    background: theme.palette.background.body,
                }}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 1.5, letterSpacing: 1.2, textAlign: 'center' }}>
                        {t.release}
                    </Typography>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '1.1rem', sm: '1.7rem', md: '2.1rem' },
                            textAlign: 'center',
                            lineHeight: 1.18,
                            mb: 1.5,
                            color: 'text.primary',
                            maxWidth: 950,
                            mx: 'auto',
                        }}
                    >
                        {t.heroTitle}
                        <Box component="span" sx={{
                            background: 'linear-gradient(90deg, #6a1b9a 40%, #8e24aa 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            color: 'transparent',
                            fontWeight: 700,
                            display: 'inline',
                        }}>
                            {t.heroTitleHighlight}
                        </Box>
                        <span style={{ display: { xs: 'inline', md: 'block' } }}>,</span>
                        <Box component="span" sx={{ display: { xs: 'inline', md: 'block' } }}>
                            {t.heroTitleEnd}
                        </Box>
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' }, mb: 3, textAlign: 'center', maxWidth: 800 }}>
                        {t.heroSubtitle}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', width: '100%', maxWidth: { xs: '100%', sm: 'auto' }, px: { xs: 2, sm: 0 } }}>
                        <Button
                            variant="solid"
                            color="primary"
                            size="md"
                            onClick={() => redirectToAuth(isRussianDomain)}
                            sx={{
                                borderRadius: 99,
                                px: 3,
                                py: 1.5,
                                fontWeight: 600,
                                fontSize: '0.98rem',
                                background: 'linear-gradient(90deg, #6a1b9a 40%, #8e24aa 100%)',
                                color: '#fff',
                                width: { xs: 'calc(100% - 32px)', sm: 'auto' },
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #7b1fa2 40%, #9c27b0 100%)',
                                    color: '#fff',
                                },
                            }}
                            endDecorator={<ArrowForward />}
                        >
                            {t.tryButton}
                        </Button>
                        <Button
                            variant="soft"
                            color="primary"
                            size="md"
                            onClick={scrollToTabs}
                            sx={{
                                borderRadius: 99,
                                px: 3,
                                py: 1.5,
                                fontWeight: 600,
                                fontSize: '0.98rem',
                                boxShadow: 'none',
                                background: 'rgba(106,27,154,0.10)',
                                color: '#6a1b9a',
                                width: { xs: 'calc(100% - 32px)', sm: 'auto' },
                                '&:hover': {
                                    background: 'rgba(142,36,170,0.18)',
                                    color: '#6a1b9a',
                                },
                            }}
                            endDecorator={<ArrowForward />}
                        >
                            {t.learnMore}
                        </Button>
                    </Box>
                </Box>

                {/* Tabbed Feature Block */}
                <Box ref={tabsRef} sx={{ py: 8, background: theme.palette.background.body }}>
                    <Stack spacing={6} sx={{ maxWidth: '1100px', mx: 'auto', px: { xs: 1, sm: 3 } }}>
                        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 3, background: 'transparent' }}>
                            <TabList variant="plain" sx={{
                                justifyContent: { xs: 'space-between', sm: 'flex-start' },
                                mb: 3,
                                width: { xs: '100%', sm: 'auto' }
                            }}>
                                {featureTabs.map((tab, idx) => (
                                    <Tab key={tab.label} sx={{ fontWeight: 600, fontSize: { xs: '0.78rem', sm: '1.1rem' }, px: 3, py: 1, borderRadius: 2 }}>{tab.label}</Tab>
                                ))}
                            </TabList>
                            {featureTabs.map((tab, idx) => (
                                <TabPanel
                                    key={tab.label}
                                    value={idx}
                                    sx={{ display: tabIndex === idx ? 'flex' : 'none', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'stretch', minHeight: 240 }}
                                >
                                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', pr: { md: 3 } }}>
                                        <Typography level="h3" sx={{ mb: 2, fontWeight: 700 }}>{tab.label}</Typography>
                                        <Typography level="body-lg" color="neutral" sx={{ mb: 2 }}>{tab.description}</Typography>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
                                            <CardContent>
                                                <Typography level="body-sm" sx={{ color: 'primary.main', fontFamily: 'monospace', mb: 1 }}>{t.input}</Typography>
                                                <Typography sx={{ fontFamily: 'monospace', fontSize: '1rem', color: 'text.primary' }}>{tab.input}</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                            <CardContent>
                                                <Typography level="body-sm" sx={{ color: 'primary.main', fontFamily: 'monospace', mb: 1 }}>{t.output}</Typography>
                                                <TypewriterText
                                                    text={tab.output}
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        fontSize: '1rem',
                                                        fontWeight: 400,
                                                        color: 'text.primary',
                                                        whiteSpace: 'pre-line',
                                                    }}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Box>
                                </TabPanel>
                            ))}
                        </Tabs>
                    </Stack>
                </Box>

                {/* Side-by-Side Model Comparison */}
                <Box sx={{ py: 8, background: theme.palette.mode === 'dark' ? '#181028' : '#f3e6fa' }}>
                    <Stack spacing={4} sx={{ maxWidth: '1100px', mx: 'auto', px: { xs: 1, sm: 3 } }}>
                        <Typography level="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 400 }}>{t.modelComparison}</Typography>
                        <Grid container spacing={4}>
                            <Grid xs={12} md={6}>
                                <Card variant="outlined" sx={{ borderRadius: 2, px: 3, pt: 3, height: '100%' }}>
                                    <CardContent>
                                        <Typography level="h4" sx={{ color: 'primary.main', mb: 2, fontWeight: 700 }}>{t.capricorn43}</Typography>
                                        {modelComparisons.map((row, idx) => (
                                            <Box key={row.label} sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: 'primary.main', fontFamily: 'monospace', mb: 1 }}>{row.label}</Typography>
                                                <Typography sx={{ fontFamily: 'monospace', fontSize: '1rem', color: 'text.primary' }}>{row.value || row.value4_3}</Typography>
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid xs={12} md={6}>
                                <Card variant="outlined" sx={{ borderRadius: 2, px: 3, pt: 3, height: '100%', borderColor: '#8e24aa' }}>
                                    <CardContent>
                                        <Typography level="h4" sx={{ color: 'primary.main', mb: 2, fontWeight: 700 }}>{t.capricorn44}</Typography>
                                        {modelComparisons.map((row, idx) => (
                                            <Box key={row.label} sx={{ mb: 2 }}>
                                                <Typography level="body-sm" sx={{ color: 'primary.main', fontFamily: 'monospace', mb: 1 }}>{row.label}</Typography>
                                                <Typography sx={{ fontFamily: 'monospace', fontSize: '1rem', color: 'text.primary' }}>{row.value || row.value4_4b}</Typography>
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Stack>
                </Box>

                {/* Stats/Percentile Block */}
                <Box id="benchmarks" sx={{ py: 8, background: theme.palette.background.body }}>
                    <Stack spacing={4} sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: 1, sm: 3 } }}>
                        <Typography level="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 400, fontSize: { xs: '1.8rem', sm: '2.5rem' } }}>
                            {t.benchmarksTitle}
                        </Typography>
                        {isMobile ? (
                            <Stack
                                spacing={4}
                                sx={{
                                    px: 3,
                                    alignItems: 'stretch'
                                }}
                            >
                                {/* Answer Speed */}
                                <Box sx={{ width: '100%', textAlign: 'left' }}>
                                    <Typography sx={{ fontWeight: 400, fontSize: '1.35rem', mb: 1, color: 'text.primary' }}>{t.answerSpeed}</Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography sx={{ fontWeight: 300, fontSize: '2.5rem', lineHeight: 1 }}>{benchmarks[0].value4_3}</Typography>
                                            <Typography sx={{ fontWeight: 300, fontSize: '1.1rem' }}>{t.capricorn43}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontWeight: 300, fontSize: '2.5rem', color: '#a084ca', lineHeight: 1 }}>{benchmarks[0].value4_4b}</Typography>
                                            <Typography sx={{ color: '#a084ca', fontWeight: 300, fontSize: '1.1rem' }}>{t.capricorn44}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                {/* Correctness */}
                                <Box sx={{ width: '100%', textAlign: 'left' }}>
                                    <Typography sx={{ fontWeight: 400, fontSize: '1.35rem', mb: 1, color: 'text.primary' }}>{t.correctness}</Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography sx={{ fontWeight: 300, fontSize: '2.5rem', lineHeight: 1 }}>{benchmarks[1].value4_3}</Typography>
                                            <Typography sx={{ fontWeight: 300, fontSize: '1.1rem' }}>{t.capricorn43}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontWeight: 300, fontSize: '2.5rem', color: '#a084ca', lineHeight: 1 }}>{benchmarks[1].value4_4b}</Typography>
                                            <Typography sx={{ color: '#a084ca', fontWeight: 300, fontSize: '1.1rem' }}>{t.capricorn44}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Stack>
                        ) : (
                            <Grid container spacing={0} alignItems="flex-start" justifyContent="center">
                                {/* Answer Speed */}
                                <Grid item xs={12} sm={6} md={3} sx={{ px: { xs: 0, sm: 2 }, mb: { xs: 4, sm: 0 } }}>
                                    <Box sx={{ textAlign: 'left', width: '100%' }}>
                                        <Typography sx={{ fontWeight: 400, fontSize: '1.35rem', mb: 2, color: 'text.primary' }}>{t.answerSpeed}</Typography>
                                        <Typography sx={{ fontWeight: 300, fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' }, lineHeight: 1 }}>{benchmarks[0].value4_3}</Typography>
                                        <Typography sx={{ fontWeight: 300, fontSize: '1.1rem', mt: 1 }}>{t.capricorn43}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3} sx={{ px: { xs: 0, sm: 2 }, mb: { xs: 4, sm: 0 } }}>
                                    <Box sx={{ textAlign: 'left', width: '100%' }}>
                                        <Box sx={{ height: '2.2rem', mb: 2 }} /> {/* Spacer for alignment */}
                                        <Typography sx={{ fontWeight: 300, fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' }, color: '#a084ca', lineHeight: 1 }}>{benchmarks[0].value4_4b}</Typography>
                                        <Typography sx={{ color: '#a084ca', fontWeight: 300, fontSize: '1.1rem', mt: 1 }}>{t.capricorn44}</Typography>
                                    </Box>
                                </Grid>
                                {/* Correctness */}
                                <Grid item xs={12} sm={6} md={3} sx={{ px: { xs: 0, sm: 2 }, mb: { xs: 4, sm: 0 } }}>
                                    <Box sx={{ textAlign: 'left', width: '100%' }}>
                                        <Typography sx={{ fontWeight: 400, fontSize: '1.35rem', mb: 2, color: 'text.primary' }}>{t.correctness}</Typography>
                                        <Typography sx={{ fontWeight: 300, fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' }, lineHeight: 1 }}>{benchmarks[1].value4_3}</Typography>
                                        <Typography sx={{ fontWeight: 300, fontSize: '1.1rem', mt: 1 }}>{t.capricorn43}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3} sx={{ px: { xs: 0, sm: 2 }, mb: { xs: 4, sm: 0 } }}>
                                    <Box sx={{ textAlign: 'left', width: '100%' }}>
                                        <Box sx={{ height: '2.2rem', mb: 2 }} /> {/* Spacer for alignment */}
                                        <Typography sx={{ fontWeight: 300, fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' }, color: '#a084ca', lineHeight: 1 }}>{benchmarks[1].value4_4b}</Typography>
                                        <Typography sx={{ color: '#a084ca', fontWeight: 300, fontSize: '1.1rem', mt: 1 }}>{t.capricorn44}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                    </Stack>
                </Box>

                {/* Pricing Block */}
                <Box sx={{
                    py: 8,
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(120deg, #181028 60%, #221a33 100%)'
                        : 'linear-gradient(120deg, #f3e6fa 60%, #ede7f6 100%)',
                }} data-pricing-block>
                    <Stack spacing={2} sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: 1, sm: 3 }, alignItems: 'center' }}>
                        <Stack spacing={1} sx={{ pb: 2 }}>
                            <Typography level="h1" sx={{ textAlign: 'center', fontWeight: 700, fontSize: { xs: '1.6rem', sm: '2.2rem' }, mb: 1, letterSpacing: 0.5 }}>
                                {t.pricingTitle}
                            </Typography>
                            <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '1.1rem' }, mb: 4, fontWeight: 400 }}>
                                {t.pricingSubtitle}
                            </Typography>
                        </Stack>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: { xs: 2, sm: 4 },
                                overflowX: 'auto',
                                userSelect: 'none',
                                WebkitOverflowScrolling: 'touch',
                                scrollbarWidth: 'none',
                                '&::-webkit-scrollbar': { display: 'none' },
                                maxWidth: '100vw',
                            }}
                        >
                            {/* Free Plan */}
                            <Box sx={{ flex: '0 0 320px', minWidth: 280, maxWidth: 340, display: 'flex', ml: { xs: 2, sm: 0 } }}>
                                <Card variant="outlined" sx={{
                                    borderRadius: 4,
                                    minHeight: 420,
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    background: theme.palette.mode === 'dark' ? '#221a33' : '#fff',
                                    border: '2px solid',
                                    borderColor: theme.palette.mode === 'dark' ? 'rgba(179,136,255,0.13)' : '#ececec',
                                    p: 3,
                                }}>
                                    <Typography level="h3" sx={{ fontWeight: 600, fontSize: '1.15rem', letterSpacing: 0.2 }}>{t.planFree}</Typography>
                                    <AnimatedPrice planKey="free" />
                                    <Typography sx={{ color: 'text.secondary', mb: 2, fontSize: '1rem' }}>{t.freeDesc}</Typography>
                                    <Stack spacing={1} sx={{ width: '100%', mb: 2, flexGrow: 1 }}>
                                        {t.freeFeatures.map((f, i) => (
                                            <Stack direction="row" spacing={1} alignItems="flex-start" key={i}>
                                                <CheckCircle sx={{ color: '#7c4dff', fontSize: 20, mt: '2px' }} />
                                                <Typography sx={{ fontSize: '1rem', lineHeight: 1.4 }}>{f}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Button onClick={() => redirectToAuth(isRussianDomain)} variant="solid" color="primary" fullWidth sx={{
                                        borderRadius: 99,
                                        fontWeight: 600,
                                        fontSize: '0.98rem',
                                        background: 'linear-gradient(90deg, #6a1b9a 40%, #8e24aa 100%)',
                                        color: '#fff',
                                        boxShadow: 'none',
                                        py: 1.5,
                                        mt: 'auto',
                                        transition: 'background 0.2s',
                                        '&:hover': {
                                            background: 'linear-gradient(90deg, #7b1fa2 40%, #9c27b0 100%)',
                                            color: '#fff',
                                        },
                                    }} startDecorator={<AutoAwesome />}>{t.tryButton}</Button>
                                </Card>
                            </Box>
                            {/* Smart Plan */}
                            <Box sx={{ flex: '0 0 320px', minWidth: 280, maxWidth: 340, display: 'flex' }}>
                                <Card variant="outlined" sx={{
                                    borderRadius: 4,
                                    minHeight: 420,
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    background: theme.palette.mode === 'dark' ? '#221a33' : '#fff',
                                    border: '2px solid',
                                    borderColor: theme.palette.mode === 'dark' ? 'rgba(179,136,255,0.13)' : '#ececec',
                                    p: 3,
                                }}>
                                    <Typography level="h3" sx={{ fontWeight: 600, fontSize: '1.15rem', letterSpacing: 0.2 }}>{t.planSmart}</Typography>
                                    <AnimatedPrice planKey="smart" />
                                    <Typography sx={{ color: 'text.secondary', mb: 2, fontSize: '1rem' }}>{t.smartDesc}</Typography>
                                    <Stack spacing={1} sx={{ width: '100%', mb: 2, flexGrow: 1 }}>
                                        {t.smartFeatures.map((f, i) => (
                                            <Stack direction="row" spacing={1} alignItems="flex-start" key={i}>
                                                <CheckCircle sx={{ color: '#7c4dff', fontSize: 20, mt: '2px' }} />
                                                <Typography sx={{ fontSize: '1rem', lineHeight: 1.4 }}>{f}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Button onClick={() => redirectToAuth(isRussianDomain, 'plans')} variant="solid" color="primary" fullWidth sx={{
                                        borderRadius: 99,
                                        fontWeight: 600,
                                        fontSize: '0.98rem',
                                        background: 'linear-gradient(90deg, #6a1b9a 40%, #8e24aa 100%)',
                                        color: '#fff',
                                        boxShadow: 'none',
                                        py: 1.5,
                                        mt: 'auto',
                                        transition: 'background 0.2s',
                                        '&:hover': {
                                            background: 'linear-gradient(90deg, #7b1fa2 40%, #9c27b0 100%)',
                                            color: '#fff',
                                        },
                                    }}>{t.getStarted}</Button>
                                </Card>
                            </Box>
                            {/* Ultimate Plan */}
                            <Box sx={{ flex: '0 0 320px', minWidth: 280, maxWidth: 340, display: 'flex' }}>
                                <Card variant="outlined" sx={{
                                    borderRadius: 4,
                                    minHeight: 420,
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    background: theme.palette.mode === 'dark' ? '#221a33' : '#fff',
                                    border: '2px solid',
                                    borderColor: theme.palette.mode === 'dark' ? 'rgba(179,136,255,0.13)' : '#ececec',
                                    p: 3,
                                }}>
                                    <Typography level="h3" sx={{ fontWeight: 600, fontSize: '1.15rem', letterSpacing: 0.2 }}>{t.planUltimate}</Typography>
                                    <AnimatedPrice planKey="ultimate" />
                                    <Typography sx={{ color: 'text.secondary', mb: 2, fontSize: '1rem' }}>{t.ultimateDesc}</Typography>
                                    <Stack spacing={1} sx={{ width: '100%', mb: 2, flexGrow: 1 }}>
                                        {t.ultimateFeatures.map((f, i) => (
                                            <Stack direction="row" spacing={1} alignItems="flex-start" key={i}>
                                                <CheckCircle sx={{ color: '#7c4dff', fontSize: 20, mt: '2px' }} />
                                                <Typography sx={{ fontSize: '1rem', lineHeight: 1.4 }}>{f}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Button onClick={() => redirectToAuth(isRussianDomain, 'plans')}  variant="solid" color="primary" fullWidth sx={{
                                        borderRadius: 99,
                                        fontWeight: 600,
                                        fontSize: '0.98rem',
                                        background: 'linear-gradient(90deg, #6a1b9a 40%, #8e24aa 100%)',
                                        color: '#fff',
                                        boxShadow: 'none',
                                        py: 1.5,
                                        mt: 'auto',
                                        transition: 'background 0.2s',
                                        '&:hover': {
                                            background: 'linear-gradient(90deg, #7b1fa2 40%, #9c27b0 100%)',
                                            color: '#fff',
                                        },
                                    }}>{t.getStarted}</Button>
                                </Card>
                            </Box>
                            {/* Advanced Plan */}
                            <Box sx={{ flex: '0 0 320px', minWidth: 280, maxWidth: 340, display: 'flex', mr: { xs: 2, sm: 0 } }}>
                                <Card variant="outlined" sx={{
                                    borderRadius: 4,
                                    minHeight: 420,
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    background: theme.palette.mode === 'dark' ? '#221a33' : '#fff',
                                    border: '2px solid',
                                    borderColor: theme.palette.mode === 'dark' ? 'rgba(179,136,255,0.13)' : '#ececec',
                                    p: 3,
                                }}>
                                    <Typography level="h3" sx={{ fontWeight: 600, fontSize: '1.15rem', letterSpacing: 0.2 }}>{t.planAdvanced}</Typography>
                                    <AnimatedPrice planKey="advanced" />
                                    <Typography sx={{ color: 'text.secondary', mb: 2, fontSize: '1rem' }}>{t.advancedDesc}</Typography>
                                    <Stack spacing={1} sx={{ width: '100%', mb: 2, flexGrow: 1 }}>
                                        {t.advancedFeatures.map((f, i) => (
                                            <Stack direction="row" spacing={1} alignItems="flex-start" key={i}>
                                                <CheckCircle sx={{ color: '#7c4dff', fontSize: 20, mt: '2px' }} />
                                                <Typography sx={{ fontSize: '1rem', lineHeight: 1.4 }}>{f}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Button onClick={() => redirectToAuth(isRussianDomain, 'plans')} variant="solid" color="primary" fullWidth sx={{
                                        borderRadius: 99,
                                        fontWeight: 600,
                                        fontSize: '0.98rem',
                                        background: 'linear-gradient(90deg, #6a1b9a 40%, #8e24aa 100%)',
                                        color: '#fff',
                                        boxShadow: 'none',
                                        py: 1.5,
                                        mt: 'auto',
                                        transition: 'background 0.2s',
                                        '&:hover': {
                                            background: 'linear-gradient(90deg, #7b1fa2 40%, #9c27b0 100%)',
                                            color: '#fff',
                                        },
                                    }}>{t.getStarted}</Button>
                                </Card>
                            </Box>
                        </Box>
                    </Stack>
                </Box>

                {/* Features Section */}
                <Box sx={{ py: 8, background: theme.palette.background.body }}>
                    <Stack spacing={8} sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: 1, sm: 3 } }}>
                        {/* More Capable */}
                        <Grid container spacing={4} alignItems="flex-start">
                            <Grid item xs={12} md={6}>
                                <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 2, color: 'text.primary' }}>{t.superiorUnderstanding}</Typography>
                                <Typography sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, color: 'text.secondary' }}>
                                    {t.superiorUnderstandingDesc}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{
                                    background: 'linear-gradient(120deg, rgba(142,36,170,0.18) 60%, rgba(106,27,154,0.12) 100%)',
                                    borderRadius: 4,
                                    p: { xs: 3, sm: 4 },
                                    color: 'text.primary',
                                    fontSize: '1.15rem',
                                    boxShadow: '0 2px 16px 0 rgba(142,36,170,0.08)',
                                }}>
                                    <Typography sx={{ fontSize: '1.15rem', mb: 2, color: 'text.primary' }}>
                                        {t.testimonial1}
                                    </Typography>
                                    <Typography sx={{ color: 'text.secondary', fontSize: '1rem', mt: 2 }}>
                                        {t.testimonial1Author}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider sx={{ my: 2, borderColor: 'divider' }} />
                        {/* More Creative */}
                        <Grid container spacing={4} alignItems="flex-start">
                            <Grid item xs={12} md={6}>
                                <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 2, color: 'text.primary' }}>{t.versatileExcellence}</Typography>
                                <Typography sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, color: 'text.secondary' }}>
                                    {t.versatileExcellenceDesc}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{
                                    background: 'linear-gradient(120deg, rgba(142,36,170,0.18) 60%, rgba(106,27,154,0.12) 100%)',
                                    borderRadius: 4,
                                    p: { xs: 3, sm: 4 },
                                    color: 'text.primary',
                                    fontSize: '1.15rem',
                                    boxShadow: '0 2px 16px 0 rgba(142,36,170,0.08)',
                                }}>
                                    <Typography sx={{ fontSize: '1.15rem', mb: 2, color: 'text.primary' }}>
                                        {t.testimonial2}
                                    </Typography>
                                    <Typography sx={{ color: 'text.secondary', fontSize: '1rem', mt: 2 }}>
                                        {t.testimonial2Author}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider sx={{ my: 2, borderColor: 'divider' }} />
                        {/* Vast Knowledge */}
                        <Grid container spacing={4} alignItems="flex-start">
                            <Grid item xs={12} md={6}>
                                <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 2, color: 'text.primary' }}>{t.developerExperience}</Typography>
                                <Typography sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, color: 'text.secondary' }}>
                                    {t.developerExperienceDesc}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{
                                    background: 'linear-gradient(120deg, rgba(142,36,170,0.18) 60%, rgba(106,27,154,0.12) 100%)',
                                    borderRadius: 4,
                                    p: { xs: 3, sm: 4 },
                                    color: 'text.primary',
                                    fontSize: '1.15rem',
                                    boxShadow: '0 2px 16px 0 rgba(142,36,170,0.08)',
                                }}>
                                    <Typography sx={{ fontSize: '1.15rem', mb: 2, color: 'text.primary' }}>
                                        {t.testimonial3}
                                    </Typography>
                                    <Typography sx={{ color: 'text.secondary', fontSize: '1rem', mt: 2 }}>
                                        {t.testimonial3Author}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Stack>
                </Box>

                {/* CTA Section */}
                <Box id="cta" sx={{
                    py: 8,
                    background: theme.palette.mode === 'dark' ? '#181028' : '#f3e6fa',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <Stack spacing={2} sx={{ maxWidth: 800, mx: 'auto', alignItems: 'center', textAlign: 'center' }}>
                        <Typography
                            sx={{
                                fontWeight: 500,
                                fontSize: { xs: '1.5rem', sm: '1.8rem' },
                                color: 'text.primary',
                                mb: 0.5,
                            }}
                        >
                            {t.readyToExperience}
                        </Typography>
                        <Typography
                            sx={{
                                color: 'text.secondary',
                                fontSize: '1rem',
                                mb: 3,
                                fontWeight: 400,
                            }}
                        >
                            {t.joinThousands}
                        </Typography>
                        <Button
                            size="md"
                            variant="solid"
                            color="primary"
                            onClick={() => redirectToAuth(isRussianDomain)}
                            endDecorator={<ArrowForward />}
                            sx={{
                                borderRadius: 99,
                                px: 3,
                                py: 1.5,
                                fontWeight: 500,
                                fontSize: '0.98rem',
                                background: 'linear-gradient(90deg, #6a1b9a 40%, #8e24aa 100%)',
                                color: '#fff',
                                boxShadow: 'none',
                                transition: 'background 0.2s',
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #7b1fa2 40%, #9c27b0 100%)',
                                    color: '#fff',
                                },
                            }}
                        >
                            {t.getStarted}
                        </Button>
                    </Stack>
                </Box>

                {/* Сообщения Capricorn AI над футером */}
                <AnimatePresence>
                    {inputVisible && expanded && (response || error || count >= 5) && (
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 40 }}
                            transition={{ duration: 0.35 }}
                            style={{
                                position: 'fixed',
                                left: 0,
                                right: 0,
                                bottom: 90,
                                zIndex: 1200,
                                display: 'flex',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                            }}
                        >
                            <Box sx={{
                                maxWidth: 500,
                                width: '100%',
                                px: 2,
                                pointerEvents: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}>
                                {(count < 5 && response) && (
                                    <motion.div
                                        key={count}
                                        initial={{ opacity: 0, y: 24 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 24 }}
                                        transition={{ duration: 0.35 }}
                                        style={{ width: '100%' }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: theme.palette.mode === 'dark' ? 'rgba(35,32,43,0.85)' : 'rgba(247,247,250,0.85)',
                                            border: '1.5px solid rgba(160,132,202,0.55)',
                                            borderRadius: 99,
                                            boxShadow: theme.palette.mode === 'dark' ? '0 2px 16px 0 rgba(142,36,170,0.18)' : '0 2px 12px rgba(160,132,202,0.10)',
                                            backdropFilter: 'blur(16px)',
                                            WebkitBackdropFilter: 'blur(16px)',
                                            px: 2.5,
                                            py: 1.2,
                                            minHeight: 56,
                                            gap: 1.5,
                                        }}>
                                            <Box sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                background: 'linear-gradient(90deg, #6a1b9a 40%, #8e24aa 100%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#fff',
                                                fontWeight: 700,
                                                fontSize: 20,
                                            }}>
                                                <BubbleChart sx={{ color: '#fff', fontSize: 22 }} />
                                            </Box>
                                            <Typography
                                                noWrap
                                                sx={{
                                                    fontSize: '1.08rem',
                                                    fontWeight: 400,
                                                    color: theme.palette.text.primary,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    flex: 1,
                                                }}
                                            >
                                                {response}
                                            </Typography>
                                        </Box>
                                    </motion.div>
                                )}
                                {(count >= 5) && (
                                    <motion.div
                                        key={count}
                                        initial={{ opacity: 0, y: 24 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 24 }}
                                        transition={{ duration: 0.35 }}
                                        style={{ width: '100%' }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            background: theme.palette.mode === 'dark' ? 'rgba(35,32,43,0.85)' : 'rgba(247,247,250,0.85)',
                                            border: '1.5px solid rgba(160,132,202,0.55)',
                                            borderRadius: 99,
                                            boxShadow: theme.palette.mode === 'dark' ? '0 2px 16px 0 rgba(142,36,170,0.18)' : '0 2px 12px rgba(160,132,202,0.10)',
                                            backdropFilter: 'blur(16px)',
                                            WebkitBackdropFilter: 'blur(16px)',
                                            px: 2.5,
                                            py: 1.2,
                                            minHeight: 56,
                                            gap: 1.5,
                                        }}>
                                            <Box sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                background: 'linear-gradient(90deg, #6a1b9a 40%, #8e24aa 100%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#fff',
                                                fontWeight: 700,
                                                fontSize: 20,
                                            }}>
                                                <BubbleChart sx={{ color: '#fff', fontSize: 22 }} />
                                            </Box>
                                            <Typography sx={{ fontSize: '1.08rem', fontWeight: 400, color: theme.palette.text.primary, whiteSpace: 'pre-line', flex: 1 }}>{registerMsg}</Typography>
                                        </Box>
                                    </motion.div>
                                )}
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 24 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 24 }}
                                        transition={{ duration: 0.35 }}
                                        style={{ width: '100%' }}
                                    >
                                        <Box sx={{
                                            px: 2.5,
                                            py: 1.2,
                                            borderRadius: 99,
                                            background: theme.palette.mode === 'dark' ? 'rgba(35,32,43,0.85)' : 'rgba(247,247,250,0.85)',
                                            border: '1.5px solid #e57373',
                                            boxShadow: theme.palette.mode === 'dark' ? '0 2px 16px 0 rgba(142,36,170,0.10)' : '0 2px 12px rgba(160,132,202,0.06)',
                                            backdropFilter: 'blur(16px)',
                                            WebkitBackdropFilter: 'blur(16px)',
                                            color: '#e57373',
                                            textAlign: 'center',
                                            fontWeight: 500,
                                        }}>
                                            {error}
                                        </Box>
                                    </motion.div>
                                )}
                            </Box>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Фиксированный инпут */}
                {inputVisible && (
                    <FixedAskBlock
                        onSend={handleCapricornSubmit}
                        loading={loading}
                        disabled={count >= 5}
                        onExpandChange={handleExpandChange}
                    />
                )}

                {/* Simple Footer */}
                <Box
                    id="footer"
                    sx={{
                        backgroundColor: theme.palette.mode === 'dark' ? '#0a0a0a' : '#f7f7fa',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        py: 3,
                    }}
                >
                    <Container maxWidth="xl">
                        {isRussianDomain ? (
                            <Stack spacing={3} sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
                                <Box sx={{
                                    px: 2,
                                    pt: 2,
                                    borderRadius: 2,
                                }}>
                                    <Typography sx={{ color: 'text.primary', fontSize: '1.1rem', fontWeight: 500, mb: 1 }}>
                                        {t.footerCompany}
                                    </Typography>
                                    <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', mb: 0.5 }}>
                                        {t.footerUnp}
                                    </Typography>
                                    <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', mb: 0.5 }}>
                                        {t.footerAddress}
                                    </Typography>
                                    <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                                        {t.footerRegistration}
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    p: 2,
                                    borderRadius: 2,
                                }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                            <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                                                {t.footerEmail}
                                            </Typography>
                                            <Typography
                                                component="a"
                                                href={`mailto:${t.footerEmailValue}`}
                                                sx={{
                                                    color: 'text.primary',
                                                    fontSize: '0.9rem',
                                                    fontWeight: 500,
                                                    textDecoration: 'none',
                                                    '&:hover': {
                                                        textDecoration: 'underline'
                                                    }
                                                }}
                                            >
                                                {t.footerEmailValue}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                            <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                                                {t.footerPhone}
                                            </Typography>
                                            <Typography
                                                component="a"
                                                href={`tel:${t.footerPhoneValue.replace(/[^0-9+]/g, '')}`}
                                                sx={{
                                                    color: 'text.primary',
                                                    fontSize: '0.9rem',
                                                    fontWeight: 500,
                                                    textDecoration: 'none',
                                                    '&:hover': {
                                                        textDecoration: 'underline'
                                                    }
                                                }}
                                            >
                                                {t.footerPhoneValue}
                                            </Typography>
                                        </Stack>
                                        <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                                            {t.footerWorkingHours}
                                        </Typography>
                                    </Stack>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: 1,
                                    pb: 2,
                                    width: '100%',
                                }}>
                                    <Link onClick={e => { e.preventDefault(); setOpenPublic(true); }} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', fontSize: '1rem', fontWeight: 500, textDecoration: 'none', transition: 'color 0.2s', '&:hover': { color: 'primary.dark', textDecoration: 'underline' } }}>
                                        Публичный договор
                                    </Link>
                                    <Link onClick={e => { e.preventDefault(); setOpenPayment(true); }} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', fontSize: '1rem', fontWeight: 500, textDecoration: 'none', transition: 'color 0.2s', '&:hover': { color: 'primary.dark', textDecoration: 'underline' } }}>
                                        Оплата и возврат
                                    </Link>
                                    <Link onClick={e => { e.preventDefault(); setOpenPolicy(true); }} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', fontSize: '1rem', fontWeight: 500, textDecoration: 'none', transition: 'color 0.2s', '&:hover': { color: 'primary.dark', textDecoration: 'underline' } }}>
                                        Политика в отношении обработки персональных данных
                                    </Link>
                                    <Link onClick={e => { e.preventDefault(); setOpenConsent(true); }} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', fontSize: '1rem', fontWeight: 500, textDecoration: 'none', transition: 'color 0.2s', '&:hover': { color: 'primary.dark', textDecoration: 'underline' } }}>
                                        Согласие на обработку персональных данных
                                    </Link>
                                </Box>
                                {/* Bank Logos Grid */}
                                <Grid container spacing={2} justifyContent="center" sx={{ mb: 2 }}>
                                    {theme.palette.mode === 'dark' ? (
                                        // White theme logos
                                        [1, 2, 3, 4, 5, 6, 8, 9, 10].map((num) => (
                                            <Grid item key={num}>
                                                <Box
                                                    component="img"
                                                    src={`/img/white/white-${String(num).padStart(2, '0')}.png`}
                                                    alt={`Bank Logo ${num}`}
                                                    sx={{
                                                        height: 24,
                                                        opacity: 0.7,
                                                        transition: 'all 0.2s ease-in-out',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            filter: 'drop-shadow(0 0 8px rgba(142, 36, 170, 0.6))',
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                        ))
                                    ) : (
                                        // Black theme logos
                                        [11, 12, 13, 14, 15, 16, 18, 19, 20].map((num) => (
                                            <Grid item key={num}>
                                                <Box
                                                    component="img"
                                                    src={`/img/black/Black-${String(num).padStart(2, '0')}.png`}
                                                    alt={`Bank Logo ${num}`}
                                                    sx={{
                                                        height: 24,
                                                        opacity: 0.7,
                                                        transition: 'all 0.2s ease-in-out',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            filter: 'drop-shadow(0 0 8px rgba(142, 36, 170, 0.6))',
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                        ))
                                    )}
                                </Grid>
                                <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                                    {t.footerCopyright}
                                </Typography>
                            </Stack>
                        ) : (
                            <>
                                <Typography
                                    sx={{
                                        color: 'text.secondary',
                                        textAlign: 'center',
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    {t.footerCopyright}
                                </Typography>
                            </>
                        )}
                    </Container>
                </Box>
            </Box>

            {/* Modals for legal links (animated, fullscreen) */}
            <Transition in={openPublic} timeout={400}>
                {state => (
                    <Modal
                        keepMounted
                        open={!['exited', 'exiting'].includes(state)}
                        onClose={() => setOpenPublic(false)}
                        slotProps={{
                            backdrop: {
                                sx: {
                                    opacity: 0,
                                    backdropFilter: 'none',
                                    transition: `opacity 400ms, backdrop-filter 400ms`,
                                    ...(state === 'entering' || state === 'entered' ? { opacity: 1, backdropFilter: 'blur(8px)' } : {}),
                                },
                            },
                        }}
                        sx={{ zIndex: 10000, visibility: state === 'exited' ? 'hidden' : 'visible' }}
                    >
                        <ModalDialog variant="plain" layout={isMobile ? 'fullscreen' : 'center'} sx={{ opacity: 0, borderRadius: isMobile ? 0 : 20, maxHeight: isMobile ? null : '60vh', p: 2, transition: `opacity 300ms`, ...(state === 'entering' || state === 'entered' ? { opacity: 1 } : {}), }}>
                            <ModalClose onClick={() => setOpenPublic(false)} sx={{ borderRadius: 250 }} />
                            <DialogTitle sx={{ fontWeight: 700, fontSize: '1.35rem', mb: 0.5 }}>Публичный договор</DialogTitle>
                            <DialogContent>
                                <Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <ol style={{ paddingLeft: 18, margin: 0 }}>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Общие положения</Typography>
                                            <Typography sx={{ mb: 1 }}>
                                                Настоящий документ является официальным предложением (публичной офертой) Общества с ограниченной ответственностью «Акцептанс Групп», УНП: 193855001, адрес: 220021, Республика Беларусь, г. Минск, ул. Мичурина, д. 3, пом. 1 (далее — Оператор), заключить договор на условиях, изложенных ниже, с любым физическим лицом, принявшим условия настоящей оферты (далее — Пользователь).
                                            </Typography>
                                            <Typography sx={{ mb: 1 }}>
                                                Оферта считается акцептованной (принятой) Пользователем с момента регистрации на сайте Capricorn AI (https://chat.acceptancegroup.org) и/или оплаты подписки.
                                            </Typography>
                                            <Typography sx={{ mb: 1 }}>
                                                Оператор вправе в любое время изменять условия настоящей оферты, размещая новую редакцию на сайте. Изменения вступают в силу с момента публикации, если иное не указано в новой редакции.
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Предмет договора</Typography>
                                            <Typography sx={{ mb: 1 }}>
                                                Оператор предоставляет Пользователю доступ к сервису искусственного интеллекта Capricorn AI, включая бесплатные и платные функциональные возможности, в соответствии с выбранным тарифным планом.
                                            </Typography>
                                            <Typography sx={{ mb: 1 }}>
                                                Описание тарифных планов, их стоимость, условия предоставления и перечень услуг размещены на сайте Capricorn AI.
                                            </Typography>
                                            <Typography sx={{ mb: 1 }}>
                                                Бесплатная версия сервиса предоставляет ограниченный функционал, а платные тарифы — расширенные возможности, указанные в описании тарифов.
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Порядок заключения договора</Typography>
                                            <Typography sx={{ mb: 1 }}>
                                                Договор считается заключённым с момента акцепта оферты Пользователем, что подтверждается регистрацией на сайте и/или оплатой подписки.
                                            </Typography>
                                            <Typography sx={{ mb: 1 }}>
                                                Пользователь подтверждает, что ознакомлен и согласен с условиями настоящей оферты, политикой обработки персональных данных, условиями оплаты и возврата, размещёнными на сайте.
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Порядок оплаты и выставления счетов</Typography>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                <li><Typography component="span">Пользователь выбирает подходящий тариф на сайте Capricorn AI.</Typography></li>
                                                <li><Typography component="span">После выбора тарифа Пользователь перенаправляется на защищённую эквайринговую страницу <b>Альфа-Банк</b> для оплаты банковской картой. Все платежи осуществляются с соблюдением стандартов безопасности международных платёжных систем.</Typography></li>
                                                <li><Typography component="span">Оплата производится в белорусских рублях (BYN) или иной валюте, указанной на сайте. Все суммы, периодичность и условия платежей подробно раскрываются на странице оплаты и в личном кабинете Пользователя.</Typography></li>
                                                <li><Typography component="span">При оформлении подписки Пользователь даёт явное согласие на периодическое списание средств с его банковской карты в соответствии с выбранным тарифом (ежемесячно или за иной период).</Typography></li>
                                                <li><Typography component="span">Пользователь может в любой момент ознакомиться с историей платежей, активными подписками и условиями тарифа в личном кабинете.</Typography></li>
                                            </ul>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Пробные и бесплатные периоды</Typography>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                <li><Typography component="span">На сайте отсутствуют пробные периоды. Для новых пользователей доступен бесплатный тарифный план, условия которого (объем, ограничения, стоимость — 0 BYN/RUB/EUR/USD) раскрыты на странице тарифов.</Typography></li>
                                                <li><Typography component="span">Платные тарифы подключаются только после явного выбора и оплаты пользователем.</Typography></li>
                                            </ul>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Служба поддержки</Typography>
                                            <Typography>Для отмены подписки, возврата средств или решения других вопросов пользователь может обратиться:<br />по электронной почте: <Link href="mailto:support@acceptancegroup.org" sx={{ color: 'primary.main', fontWeight: 500 }}>support@acceptancegroup.org</Link><br />по телефону: <Link href="tel:+375447023412" sx={{ color: 'primary.main', fontWeight: 500 }}>+375 (44) 702-34-12</Link><br />или через личный кабинет на сайте Capricorn AI</Typography>
                                        </li>
                                    </ol>
                                </Box>
                            </DialogContent>
                        </ModalDialog>
                    </Modal>
                )}
            </Transition>
            {/* Модалка Оплата и возврат */}
            <Transition in={openPayment} timeout={400}>
                {state => (
                    <Modal
                        keepMounted
                        open={!['exited', 'exiting'].includes(state)}
                        onClose={() => setOpenPayment(false)}
                        slotProps={{
                            backdrop: {
                                sx: {
                                    opacity: 0,
                                    backdropFilter: 'none',
                                    transition: `opacity 400ms, backdrop-filter 400ms`,
                                    ...(state === 'entering' || state === 'entered' ? { opacity: 1, backdropFilter: 'blur(8px)' } : {}),
                                },
                            },
                        }}
                        sx={{ zIndex: 10000, visibility: state === 'exited' ? 'hidden' : 'visible' }}
                    >
                        <ModalDialog variant="plain" layout={isMobile ? 'fullscreen' : 'center'} sx={{ opacity: 0, borderRadius: isMobile ? 0 : 20, maxHeight: isMobile ? null : '60vh', p: 2, transition: `opacity 300ms`, ...(state === 'entering' || state === 'entered' ? { opacity: 1 } : {}), }}>
                            <ModalClose onClick={() => setOpenPayment(false)} sx={{ borderRadius: 250 }} />
                            <DialogTitle sx={{ fontWeight: 700, fontSize: '1.35rem', mb: 0.5 }}>Оплата и возврат</DialogTitle>
                            <DialogContent>
                                <Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <ol style={{ paddingLeft: 18, margin: 0 }}>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Общие положения</Typography>
                                            <Typography sx={{ mb: 1 }}>
                                                Пользователь может приобрести платную подписку на сервис Capricorn AI сроком на месяц или более. Также доступна полностью бесплатная версия сервиса с базовым функционалом.
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Порядок оплаты и выставления счетов</Typography>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                <li><Typography component="span">Пользователь выбирает подходящий тариф на сайте Capricorn AI.</Typography></li>
                                                <li><Typography component="span">После выбора тарифа пользователь перенаправляется на защищённую эквайринговую страницу Альфа-Банк для оплаты банковской картой.</Typography></li>
                                                <li><Typography component="span">При оформлении подписки пользователь соглашается с условиями периодического выставления счетов (ежемесячно или за выбранный период).</Typography></li>
                                                <li><Typography component="span">Сумма и периодичность платежей указываются на странице оплаты и в личном кабинете пользователя.</Typography></li>
                                            </ul>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Согласие пользователя</Typography>
                                            <Typography sx={{ mb: 1 }}>
                                                Оформляя подписку, пользователь дает явное согласие на списание средств с его банковской карты. Согласие включает: сумму операции, периодичность платежей (например, ежемесячно), срок действия разрешения (до отмены пользователем). Пользователь может отменить подписку в любой момент через личный кабинет.
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Отмена подписки и возврат средств</Typography>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                <li><Typography component="span">Пользователь может отменить подписку в любой момент в личном кабинете без необходимости обращения в поддержку.</Typography></li>
                                                <li><Typography component="span">После отмены подписки дальнейшие списания не производятся.</Typography></li>
                                                <li><Typography component="span">Возврат средств за неиспользованный период не осуществляется, если иное не предусмотрено индивидуальными условиями или законодательством.</Typography></li>
                                                <li><Typography component="span">В случае ошибочного списания или технической ошибки возврат осуществляется после обращения в службу поддержки.</Typography></li>
                                            </ul>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Пробные и бесплатные периоды</Typography>
                                            <Typography sx={{ mb: 1 }}>
                                                Пробных периодов нет. Для новых пользователей доступен бесплатный тарифный план, условия которого (объем, ограничения, стоимость — 0 BYN/RUB/EUR/USD) раскрыты на странице тарифов. Платные тарифы подключаются только после явного выбора и оплаты пользователем.
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Служба поддержки</Typography>
                                            <Typography>
                                                Для отмены подписки, возврата средств или решения других вопросов пользователь может обратиться:<br />
                                                по электронной почте: <Link href="mailto:support@acceptancegroup.org" sx={{ color: 'primary.main', fontWeight: 500 }}>support@acceptancegroup.org</Link><br />
                                                по телефону: <Link href="tel:+375447023412" sx={{ color: 'primary.main', fontWeight: 500 }}>+375 (44) 702-34-12</Link><br />
                                                или через личный кабинет на сайте Capricorn AI
                                            </Typography>
                                        </li>
                                    </ol>
                                </Box>
                            </DialogContent>
                        </ModalDialog>
                    </Modal>
                )}
            </Transition>
            <Transition in={openPolicy} timeout={400}>
                {state => (
                    <Modal
                        keepMounted
                        open={!['exited', 'exiting'].includes(state)}
                        onClose={() => setOpenPolicy(false)}
                        slotProps={{
                            backdrop: {
                                sx: {
                                    opacity: 0,
                                    backdropFilter: 'none',
                                    transition: `opacity 400ms, backdrop-filter 400ms`,
                                    ...(state === 'entering' || state === 'entered' ? { opacity: 1, backdropFilter: 'blur(8px)' } : {}),
                                },
                            },
                        }}
                        sx={{ zIndex: 10000, visibility: state === 'exited' ? 'hidden' : 'visible' }}
                    >
                        <ModalDialog variant="plain" layout={isMobile ? 'fullscreen' : 'center'} sx={{ opacity: 0, borderRadius: isMobile ? 0 : 20, maxHeight: isMobile ? null : '60vh', p: 2, transition: `opacity 300ms`, ...(state === 'entering' || state === 'entered' ? { opacity: 1 } : {}), }}>
                            <ModalClose onClick={() => setOpenPolicy(false)} sx={{ borderRadius: 250 }} />
                            <DialogTitle sx={{ fontWeight: 700, fontSize: '1.35rem', mb: 0.5 }}>Политика в отношении обработки персональных данных</DialogTitle>
                            <DialogContent>
                                <Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <Typography sx={{ mb: 2, fontSize: '1.05rem' }}>
                                        Настоящая Политика определяет порядок и условия обработки персональных данных пользователей сайта Capricorn AI (<b>https://chat.acceptancegroup.org</b>) <b>Обществом с ограниченной ответственностью "Акцептанс Групп"</b>, УНП: 193855001, адрес: <b>220021, Республика Беларусь, г. Минск, ул. Мичурина, д. 3, пом. 1</b> (далее — Оператор).
                                    </Typography>
                                    <ol style={{ paddingLeft: 18, margin: 0 }}>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Общие положения</Typography>
                                            <Typography sx={{ mb: 1 }}>
                                                Оператор обеспечивает защиту персональных данных, обрабатываемых в рамках своей деятельности, и соблюдение прав субъектов персональных данных в соответствии с законодательством Республики Беларусь.
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Цели и правовые основания обработки персональных данных</Typography>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                <li><Typography component="span">предоставление доступа к сервису искусственного интеллекта;</Typography></li>
                                                <li><Typography component="span">обеспечение функционирования и улучшения качества работы сайта и моделей ИИ;</Typography></li>
                                                <li><Typography component="span">обратная связь и поддержка пользователей;</Typography></li>
                                                <li><Typography component="span">выполнение требований законодательства.</Typography></li>
                                            </ul>
                                            <Typography sx={{ mt: 1 }}>Правовым основанием обработки персональных данных является согласие пользователя, а также иные основания, предусмотренные законодательством Республики Беларусь.</Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Категории субъектов и перечень обрабатываемых персональных данных</Typography>
                                            <Typography sx={{ mb: 1 }}>Субъектами персональных данных являются пользователи сайта Capricorn AI.</Typography>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                <li><Typography component="span">адрес электронной почты;</Typography></li>
                                                <li><Typography component="span">история чатов и сообщений, введённых на сайте;</Typography></li>
                                                <li><Typography component="span">страна проживания и иные данные, предоставленные при использовании сервиса;</Typography></li>
                                                <li><Typography component="span">техническая информация (IP-адрес, данные о браузере и устройстве).</Typography></li>
                                            </ul>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Порядок и условия обработки персональных данных</Typography>
                                            <Typography sx={{ mb: 1 }}>Персональные данные обрабатываются с использованием средств автоматизации и/или без использования таких средств.</Typography>
                                            <Typography sx={{ mb: 1 }}>Оператор осуществляет следующие действия с персональными данными:</Typography>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                <li><Typography component="span">сбор, систематизация, хранение, уточнение (обновление, изменение), использование, обезличивание, блокирование, удаление, уничтожение;</Typography></li>
                                            </ul>
                                            <Typography sx={{ mb: 1 }}>Источником получения персональных данных является сам пользователь при регистрации и использовании сайта.</Typography>
                                            <Typography sx={{ mb: 1 }}>Оператор может поручать обработку персональных данных уполномоченным лицам, обеспечивающим работу и развитие сервиса, при условии соблюдения ими требований к защите персональных данных.</Typography>
                                            <Typography sx={{ mb: 1 }}>Оператор принимает необходимые правовые, организационные и технические меры для защиты персональных данных от неправомерного или случайного доступа, уничтожения, изменения, блокирования, копирования, предоставления, распространения, а также от иных неправомерных действий.</Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Сроки хранения персональных данных</Typography>
                                            <Typography>Персональные данные хранятся в течение срока использования сайта пользователем и 5 (пяти) лет после последнего использования, либо до момента отзыва согласия пользователем.</Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Права субъектов персональных данных и механизм их реализации</Typography>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                <li><Typography component="span">получать информацию о своих персональных данных и порядке их обработки;</Typography></li>
                                                <li><Typography component="span">требовать уточнения, блокирования или уничтожения своих персональных данных, если они являются неполными, устаревшими, неточными, незаконно полученными или не являются необходимыми для заявленной цели обработки;</Typography></li>
                                                <li><Typography component="span">отозвать согласие на обработку персональных данных.</Typography></li>
                                            </ul>
                                            <Typography sx={{ mt: 1 }}>Для реализации своих прав пользователь может направить письменное заявление по адресу: <b>220021, Республика Беларусь, г. Минск, ул. Мичурина, д. 3, пом. 1</b>, либо электронное обращение на e-mail: <Link href="mailto:support@acceptancegroup.org" sx={{ color: 'primary.main', fontWeight: 500 }}>support@acceptancegroup.org</Link>.<br />или по телефону: <Link href="tel:+375447023412" sx={{ color: 'primary.main', fontWeight: 500 }}>+375 (44) 702-34-12</Link></Typography>
                                            <Typography sx={{ mt: 1 }}>Ответственным за осуществление внутреннего контроля за обработкой персональных данных является назначенное Оператором лицо, контактные данные которого указаны выше.</Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Трансграничная передача персональных данных</Typography>
                                            <Typography sx={{ mb: 1 }}>Оператор может осуществлять трансграничную передачу персональных данных в государства, обеспечивающие надлежащий уровень защиты прав субъектов персональных данных, исключительно для целей, указанных в разделе 2 настоящей Политики.</Typography>
                                            <Typography>В случае необходимости передачи данных в иные государства, Оператор обеспечивает соблюдение требований законодательства Республики Беларусь.</Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Внесение изменений в Политику</Typography>
                                            <Typography sx={{ mb: 1 }}>Оператор вправе вносить изменения в настоящую Политику. Новая редакция Политики вступает в силу с момента ее размещения на сайте, если иное не предусмотрено новой редакцией.</Typography>
                                            <Typography>В случае внесения существенных изменений в Политику, Оператор уведомляет пользователей заблаговременно до вступления изменений в силу.</Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 2, mb: 1, fontSize: '1.08rem' }}>Контактная информация</Typography>
                                            <Typography>По всем вопросам, связанным с обработкой персональных данных, пользователь может обратиться к Оператору по адресу:<br /><b>220021, Республика Беларусь, г. Минск, ул. Мичурина, д. 3, пом. 1</b><br />или по электронной почте: <Link href="mailto:support@acceptancegroup.org" sx={{ color: 'primary.main', fontWeight: 500 }}>support@acceptancegroup.org</Link><br />или по телефону: <Link href="tel:+375447023412" sx={{ color: 'primary.main', fontWeight: 500 }}>+375 (44) 702-34-12</Link></Typography>
                                        </li>
                                    </ol>
                                </Box>
                            </DialogContent>
                        </ModalDialog>
                    </Modal>
                )}
            </Transition>
            <Transition in={openConsent} timeout={400}>
                {state => (
                    <Modal
                        keepMounted
                        open={!['exited', 'exiting'].includes(state)}
                        onClose={() => setOpenConsent(false)}
                        slotProps={{
                            backdrop: {
                                sx: {
                                    opacity: 0,
                                    backdropFilter: 'none',
                                    transition: `opacity 400ms, backdrop-filter 400ms`,
                                    ...(state === 'entering' || state === 'entered' ? { opacity: 1, backdropFilter: 'blur(8px)' } : {}),
                                },
                            },
                        }}
                        sx={{ zIndex: 10000, visibility: state === 'exited' ? 'hidden' : 'visible' }}
                    >
                        <ModalDialog variant="plain" layout={isMobile ? 'fullscreen' : 'center'} sx={{ opacity: 0, borderRadius: isMobile ? 0 : 20, maxHeight: isMobile ? null : '60vh', p: 2, transition: `opacity 300ms`, ...(state === 'entering' || state === 'entered' ? { opacity: 1 } : {}), }}>
                            <ModalClose onClick={() => setOpenConsent(false)} sx={{ borderRadius: 250 }} />
                            <DialogTitle sx={{ fontWeight: 700, fontSize: '1.35rem', mb: 0.5 }}>Согласие на обработку персональных данных</DialogTitle>
                            <DialogContent>
                                <Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <Typography sx={{ mb: 2, fontSize: '1.05rem' }}>
                                        Настоящим я, пользователь сайта Capricorn AI (<b>https://chat.acceptancegroup.org</b>), даю свое согласие <b>Обществу с ограниченной ответственностью "Акцептанс Групп"</b>, УНП: 193855001, адрес: <b>220021, Республика Беларусь, г. Минск, ул. Мичурина, д. 3, пом. 1</b> (далее — Оператор), на обработку моих персональных данных на следующих условиях:
                                    </Typography>
                                    <ol style={{ paddingLeft: 18, margin: 0 }}>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>Цели обработки персональных данных:</Typography>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                <li><Typography component="span">предоставление доступа к сервису искусственного интеллекта;</Typography></li>
                                                <li><Typography component="span">обеспечение функционирования и улучшения качества работы сайта и моделей ИИ;</Typography></li>
                                                <li><Typography component="span">обратная связь, поддержка пользователей;</Typography></li>
                                                <li><Typography component="span">выполнение требований законодательства.</Typography></li>
                                            </ul>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>Перечень персональных данных:</Typography>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                <li><Typography component="span">адрес электронной почты;</Typography></li>
                                                <li><Typography component="span">история чатов и сообщений, введённых на сайте;</Typography></li>
                                                <li><Typography component="span">страна проживания и иные данные, предоставленные при использовании сервиса;</Typography></li>
                                                <li><Typography component="span">техническая информация (IP-адрес, данные о браузере и устройстве).</Typography></li>
                                            </ul>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>Срок действия согласия:</Typography>
                                            <Typography component="span">Согласие предоставляется на срок использования сайта и в течение 5 (пяти) лет после последнего использования, либо до момента отзыва согласия пользователем.</Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>Уполномоченные лица:</Typography>
                                            <Typography component="span">Обработка персональных данных может осуществляться как Оператором, так и уполномоченными им лицами, обеспечивающими работу и развитие сервиса.</Typography>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>Действия с персональными данными и способы обработки:</Typography>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                <li><Typography component="span">сбор, систематизация, хранение, уточнение (обновление, изменение), использование, обезличивание, блокирование, удаление, уничтожение;</Typography></li>
                                                <li><Typography component="span">обработка осуществляется с использованием средств автоматизации и/или без использования таких средств;</Typography></li>
                                                <li><Typography component="span">данные могут использоваться для обучения и совершенствования моделей ИИ, а также для аналитики и улучшения пользовательского опыта.</Typography></li>
                                            </ul>
                                        </li>
                                        <li>
                                            <Typography sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>Прочая информация:</Typography>
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                <li><Typography component="span">пользователь вправе отозвать согласие в любой момент, направив уведомление на электронную почту <Link href="mailto:support@acceptancegroup.org" sx={{ color: 'primary.main', fontWeight: 500 }}>support@acceptancegroup.org</Link> или по телефону: <Link href="tel:+375447023412" sx={{ color: 'primary.main', fontWeight: 500 }}>+375 (44) 702-34-12</Link>;</Typography></li>
                                                <li><Typography component="span">подробная информация о политике обработки персональных данных размещена на сайте в соответствующем разделе;</Typography></li>
                                                <li><Typography component="span">Оператор обязуется принимать все необходимые меры для защиты персональных данных от неправомерного доступа и разглашения.</Typography></li>
                                            </ul>
                                        </li>
                                    </ol>
                                </Box>
                            </DialogContent>
                        </ModalDialog>
                    </Modal>
                )}
            </Transition>
        </>
    );
}

const redirectToAuth = (isRussianDomain, page = null) => {
    let redirectUri;
    if (isRussianDomain) {
        redirectUri = `https://auth.acceptancegroup.org/oauth2/authorize?client_id=e4fb02ed-3bf7-41ff-861e-50b0fab21481&redirect_uri=https%3A%2F%2Fchat.acceptancegroup.org%2F${page ? `/${page}` : ''}&scope=token`;
    } else {
        redirectUri = `https://auth.screwltd.com/oauth2/authorize?client_id=e4fb02ed-3bf7-41ff-861e-50b0fab21481&redirect_uri=https%3A%2F%2Fchat.screwltd.com%2F${page ? `/${page}` : ''}&scope=token`;
    }

    window.location.href = redirectUri;
};

function FixedAskBlock({ onSend, loading, disabled, onExpandChange }) {
    const theme = useTheme();
    const [visible, setVisible] = React.useState(true);
    const [expanded, setExpanded] = React.useState(false);
    const [inputValue, setInputValue] = React.useState("");
    const inputRef = React.useRef(null);
    const blockRef = React.useRef(null);

    React.useEffect(() => {
        const footer = document.getElementById('footer');
        if (!footer) return;
        const observer = new window.IntersectionObserver(
            ([entry]) => setVisible(!entry.isIntersecting),
            { threshold: 0.1 }
        );
        observer.observe(footer);
        return () => observer.disconnect();
    }, []);

    React.useEffect(() => {
        if (expanded && inputRef.current) {
            inputRef.current.focus();
        }
        if (onExpandChange) onExpandChange(expanded);
    }, [expanded]);

    React.useEffect(() => {
        if (!expanded) return;
        function handleClickOutside(e) {
            if (blockRef.current && !blockRef.current.contains(e.target)) {
                setExpanded(false);
                setInputValue("");
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [expanded]);

    const handleExpand = () => setExpanded(true);
    const handleInputChange = (e) => setInputValue(e.target.value);
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!inputValue.trim()) return;
        onSend(inputValue);
        setInputValue("");
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 40 }}
                    transition={{ duration: 0.35 }}
                    style={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 24,
                        display: 'flex',
                        justifyContent: 'center',
                        zIndex: 1300,
                        pointerEvents: 'none',
                    }}
                >
                    <motion.div
                        ref={blockRef}
                        layout
                        transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: expanded ? 'flex-start' : 'center',
                            background: theme.palette.mode === 'dark' ? 'rgba(35,32,43,0.85)' : 'rgba(247,247,250,0.85)',
                            color: theme.palette.text.primary,
                            borderRadius: 99,
                            boxShadow: theme.palette.mode === 'dark' ? '0 2px 16px 0 rgba(142,36,170,0.18)' : '0 2px 12px rgba(160,132,202,0.10)',
                            pointerEvents: 'auto',
                            padding: expanded ? '0.5rem 0.5rem 0.5rem 1rem' : '0.5rem 0.5rem 0.5rem 1rem',
                            minWidth: expanded ? 320 : 180,
                            maxWidth: 480,
                            width: expanded ? 'calc(100% - 32px)' : 'auto',
                            backdropFilter: 'blur(16px)',
                            WebkitBackdropFilter: 'blur(16px)',
                            border: '1.5px solid rgba(160,132,202,0.55)',
                            margin: expanded ? '0 16px' : '0',
                        }}
                        onClick={!expanded ? handleExpand : undefined}
                    >
                        <form onSubmit={handleSubmit} style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <input
                                ref={inputRef}
                                type="text"
                                value={inputValue}
                                onChange={handleInputChange}
                                placeholder="Ask Capricorn AI"
                                style={{
                                    flex: 1,
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    color: theme.palette.text.primary,
                                    fontSize: '1.1rem',
                                    padding: '0.5rem 0',
                                }}
                                disabled={loading}
                            />
                            <motion.button
                                type="submit"
                                whileTap={{ scale: 0.9 }}
                                style={{
                                    marginLeft: 12,
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(90deg, #6a1b9a 40%, #8e24aa 100%)',
                                    border: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    boxShadow: theme.palette.mode === 'dark' ? '0 2px 8px 0 rgba(142,36,170,0.18)' : '0 2px 8px 0 rgba(160,132,202,0.10)',
                                    opacity: loading ? 0.6 : 1,
                                }}
                                disabled={loading}
                            >
                                <ArrowUpwardIcon style={{ color: '#fff' }} />
                            </motion.button>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default Home;
